import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from '../../../i18n/useTranslation';
import { useDisplayName } from '../../../Utils/useDisplayName';
import { ApiError } from '../../../Models/ApiModel';
import { PremiumFeature } from '../../../Models/enums';
import { DocumentService } from '../../../Services/PublicService';
import { formatDate } from '../../../Utils/DateUtil';
import { whatsAppShareUrl } from '../../../Utils/WhatsAppUtil';
import { Spinner } from '../../SharedArea/Spinner/Spinner';
import { ErrorState } from '../../SharedArea/ErrorState/ErrorState';
import { EmptyState } from '../../SharedArea/EmptyState/EmptyState';
import { ConfirmDialog } from '../../SharedArea/ConfirmDialog/ConfirmDialog';
import { Modal } from '../../SharedArea/Modal/Modal';
import { PremiumGate } from '../Premium/PremiumGate';
import { PremiumSubnav } from '../Premium/PremiumSubnav';
import './PaymentDocuments.css';

interface FormRow {
  token: string;
  label: string | null;
  note: string | null;
  createdAt: string;
  open: boolean;
  submissionCount: number;
}

interface SubmissionRow {
  id: number;
  student: string;
  createdAt: string;
  payload: Record<string, unknown> | null;
}

/**
 * The intake links a tutor has handed out, and what has come back through
 * them.
 *
 * Revoking closes a link without deleting the cards that already came through
 * it, which is the distinction that matters: a tutor who stops taking new
 * students has not stopped teaching the ones they have.
 */
export function PaymentDocuments() {
  return (
    <PremiumGate feature={PremiumFeature.IntakeForms}>
      <PaymentDocumentsInner />
    </PremiumGate>
  );
}

function PaymentDocumentsInner() {
  const { tr, language } = useTranslation();
  const displayName = useDisplayName();
  const [forms, setForms] = useState<FormRow[]>([]);
  const [submissions, setSubmissions] = useState<SubmissionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<FormRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [reading, setReading] = useState<SubmissionRow | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Submissions are allowed to fail on their own: a tutor who has handed
      // out links still needs to see them if the second call misbehaves.
      const [list, received] = await Promise.all([
        DocumentService.listIntakeForms(),
        DocumentService.listIntakeSubmissions().catch(() => ({ submissions: [] }))
      ]);
      setForms(list.forms);
      setSubmissions(received.submissions);
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.code : 'SERVER_ERROR');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void reload(); }, [reload]);

  function linkFor(token: string) {
    return `${window.location.origin}/intake/${token}`;
  }

  async function copyLink(token: string) {
    try {
      await navigator.clipboard.writeText(linkFor(token));
      setCopied(token);
      window.setTimeout(() => setCopied(null), 2000);
    } catch {
      // A browser that refuses clipboard access is not an error worth a
      // dialog; the link is on screen and can be copied by hand.
      setCopied(null);
    }
  }

  /**
   * Opened straight from the click, with no round trip in between: the link
   * is already known, and a window opened after an await is blocked.
   */
  function share(form: FormRow) {
    const message = `${tr('intakeShareMessage')}\r\n${linkFor(form.token)}`;
    window.open(whatsAppShareUrl(message), '_blank');
  }

  if (loading) return <Spinner />;
  if (error) return <ErrorState code={error} onRetry={() => void reload()} />;

  return (
    <section className="payment-documents">
      <PremiumSubnav />
      <header className="documents-header">
        <h1 className="screen-title">{tr('navIntakeForms')}</h1>
        <button type="button" className="primary-button" onClick={() => setCreating(true)}>
          {tr('newIntakeForm')}
        </button>
      </header>

      {forms.length === 0
        ? <EmptyState title={tr('noIntakeForms')} hint={tr('noIntakeFormsHint')} />
        : (
          <ul className="document-rows">
            {forms.map(form => (
              <li key={form.token} className={form.open ? 'document-row' : 'document-row is-closed'}>
                <div className="document-row-main">
                  {/* The label is what the tutor called this link. Without one
                      a handful of tokens are indistinguishable from each
                      other, which is the state the screen used to be in. */}
                  {form.label && <span className="document-row-label">{form.label}</span>}
                  <span className="document-row-link">
                    <bdi>{linkFor(form.token)}</bdi>
                  </span>
                  {form.note && <span className="document-row-note">{form.note}</span>}
                  <span className="document-row-meta">
                    <bdi>{formatDate(form.createdAt.slice(0, 10), language)}</bdi>
                    {' · '}
                    {form.open ? tr('formOpen') : tr('formClosed')}
                    {form.submissionCount > 0 && <> · {tr('formSubmissions', form.submissionCount)}</>}
                  </span>
                </div>
                <div className="document-row-actions">
                  <button type="button" className="ghost-button" onClick={() => void copyLink(form.token)}>
                    {copied === form.token ? tr('copied') : tr('copy')}
                  </button>
                  {form.open && (
                    <>
                      <button type="button" className="ghost-button" onClick={() => share(form)}>
                        {tr('shareOnWhatsApp')}
                      </button>
                      <button type="button" className="ghost-button is-destructive" onClick={() => setRevoking(form)}>
                        {tr('revokeForm')}
                      </button>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

      <section className="intake-received">
        <h2>{tr('intakeReceivedTitle')}</h2>
        {submissions.length === 0
          ? <p className="intake-quiet">{tr('intakeReceivedNone')}</p>
          : (
            <ul className="intake-cards">
              {submissions.map(submission => (
                <li key={submission.id}>
                  <button type="button" className="intake-card" onClick={() => setReading(submission)}>
                    <strong>{displayName(submission.student)}</strong>
                    <bdi>{formatDate(submission.createdAt.slice(0, 10), language)}</bdi>
                  </button>
                </li>
              ))}
            </ul>
          )}
      </section>

      {creating && (
        <CreateFormDialog
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); void reload(); }}
        />
      )}

      {reading && (
        <Modal title={displayName(reading.student)} subtitle={formatDate(reading.createdAt.slice(0, 10), language)} onClose={() => setReading(null)}>
          <dl className="intake-payload">
            {Object.entries(reading.payload ?? {}).map(([key, value]) => (
              <div key={key}>
                <dt>{key}</dt>
                <dd>{typeof value === 'object' ? JSON.stringify(value) : String(value ?? '')}</dd>
              </div>
            ))}
          </dl>
        </Modal>
      )}

      {revoking && (
        <ConfirmDialog
          title={tr('revokeForm')}
          body={tr('revokeFormBody')}
          confirmLabel={tr('revokeForm')}
          destructive
          onCancel={() => setRevoking(null)}
          onConfirm={() => {
            void DocumentService.revokeIntakeForm(revoking.token).then(() => {
              setRevoking(null);
              void reload();
            });
          }}
        />
      )}
    </section>
  );
}

/**
 * A label and a note, asked for once rather than never.
 *
 * The note is shown to whoever opens the link; the label is only ever seen by
 * the tutor. Saying so is the whole reason this is a dialog instead of a
 * button that silently mints an anonymous token.
 */
function CreateFormDialog({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { tr } = useTranslation();
  const [label, setLabel] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  async function create() {
    setBusy(true);
    try {
      await DocumentService.createIntakeForm(label.trim() || undefined, note.trim() || undefined);
      onCreated();
    } catch {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={tr('newIntakeForm')}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ghost-button" onClick={onClose}>{tr('cancel')}</button>
          <button type="button" className="primary-button" onClick={() => void create()} disabled={busy}>
            {busy ? <Spinner inline /> : tr('create')}
          </button>
        </>
      }
    >
      <div className="intake-create">
        <label className="field">
          <span>{tr('intakeLabel')}</span>
          <input className="text-input" value={label} onChange={event => setLabel(event.target.value)} />
          <small>{tr('intakeLabelHint')}</small>
        </label>
        <label className="field">
          <span>{tr('intakeNote')}</span>
          <textarea className="text-input" rows={2} value={note} onChange={event => setNote(event.target.value)} />
          <small>{tr('intakeNoteHint')}</small>
        </label>
      </div>
    </Modal>
  );
}
